import { CHARGE_TYPE } from "./constants";
import { STATUS_FILTER } from "../../../constants/statusFilter";

/**
 * Converts an UPPER_CASE or lowercase string to Title Case.
 * Example: "PERSON" → "Person", "STAY" → "Stay"
 */
export const toTitleCase = (str) => {
  if (!str) return "-";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Returns the currency symbol for a given currency code using Intl.NumberFormat.
 * Adds a trailing space when Intl returns a code-like symbol (e.g. "CHF", "AED")
 * so that formatted amounts read "CHF 55.00" rather than "CHF55.00".
 * Falls back to the code itself if the currency is not recognized.
 */
const symbolCache = {};
export const getCurrencySymbol = (currencyCode) => {
  if (!currencyCode) return "$";
  if (symbolCache[currencyCode]) return symbolCache[currencyCode];
  try {
    const parts = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
    }).formatToParts(0);
    const symbol = parts.find((part) => part.type === "currency")?.value;
    if (!symbol) {
      symbolCache[currencyCode] = `${currencyCode} `;
      return symbolCache[currencyCode];
    }
    const result = /^[A-Z]{2,}$/.test(symbol) ? `${symbol} ` : symbol;
    symbolCache[currencyCode] = result;
    return result;
  } catch {
    const fallback = `${currencyCode} `;
    symbolCache[currencyCode] = fallback;
    return fallback;
  }
};

/**
 * Formats the charge amount for display based on charge type.
 * FLAT: "$55.00 Flat Fee"
 * PERCENT: "10% of Total Charge"
 */
export const formatChargeAmount = (tax) => {
  if (!tax) return "-";
  const { chargeType, chargeAmount, currencyCode } = tax;
  if (chargeAmount == null) return "-";

  if (chargeType === CHARGE_TYPE.FLAT) {
    const symbol = getCurrencySymbol(currencyCode);
    return `${symbol}${Number(chargeAmount).toFixed(2)} Flat Fee`;
  }

  if (chargeType === CHARGE_TYPE.PERCENT) {
    const amount = Number(chargeAmount);
    const display = amount % 1 === 0 ? String(amount) : amount.toFixed(3);
    return `${display}% of Total Charge`;
  }

  return `${chargeAmount}`;
};

/**
 * Filters taxes/fees by Active/Inactive status. STATUS_FILTER.ALL (or a
 * missing filter) returns the list untouched. A missing `isActive` counts as
 * Inactive — same as the Status column and the DR rate-plan listing.
 */
export const filterTaxesByStatus = (taxes, statusFilter) => {
  if (!statusFilter || statusFilter === STATUS_FILTER.ALL) return taxes;
  const wantActive = statusFilter === STATUS_FILTER.ACTIVE;
  return taxes.filter((tax) => Boolean(tax.isActive) === wantActive);
};

/**
 * Filters taxes/fees by search query (matches against name and code).
 */
export const filterTaxesBySearch = (taxes, query) => {
  if (!query || !query.trim()) return taxes;
  const lowerQuery = query.toLowerCase().trim();
  return taxes.filter(
    (tax) =>
      tax.name?.toLowerCase().includes(lowerQuery) ||
      tax.code?.toLowerCase().includes(lowerQuery)
  );
};
